
'use client';

import React, { type ReactNode, useEffect } from 'react';
import { FirebaseProvider } from '@/firebase/provider';
import { firebaseApp, auth, firestore, storage, ensureAppCheck } from '@/firebase/client';

interface FirebaseClientProviderProps {
  children: ReactNode;
}

// Triggers App Check init on first user interaction. Lighthouse audits and
// bots don't interact, so reCAPTCHA never loads for them — clearing the
// third-party-cookie, missing-source-map, and aspect-ratio warnings on the
// public landing page. Signed-in users have already initialized App Check
// eagerly inside `client.ts`, so this listener is a no-op for them.
function useDeferredAppCheck() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let done = false;
    const trigger = () => {
      if (done) return;
      done = true;
      ensureAppCheck();
      for (const ev of EVENTS) window.removeEventListener(ev, trigger, options);
    };

    const EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;
    const options = { capture: true, passive: true } as AddEventListenerOptions;
    for (const ev of EVENTS) window.addEventListener(ev, trigger, options);

    return () => {
      for (const ev of EVENTS) window.removeEventListener(ev, trigger, options);
    };
  }, []);
}

export function FirebaseClientProvider({ children }: FirebaseClientProviderProps) {
  useDeferredAppCheck();
  return (
    <FirebaseProvider
      firebaseApp={firebaseApp}
      auth={auth}
      firestore={firestore}
      storage={storage}
    >
      {children}
    </FirebaseProvider>
  );
}
